import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Check, ArrowUp, ArrowDown, User, Layers } from "lucide-react";

export default function ChoiceModal({ pendingAction, players, currentPlayerIndex, onResolve, loading, onSurrender }) {
  const { t } = useTranslation();
  const [selected, setSelected] = useState(null);
  const [multiSelected, setMultiSelected] = useState([]);
  const [rearrangedOrder, setRearrangedOrder] = useState(null);

  if (!pendingAction) return null;

  const { type, card_name, prompt, for_player, options, data } = pendingAction;
  const isForMe = true; // Hot-seat: always resolving for the current for_player
  const actingPlayer = players[for_player];

  const handleSubmit = () => {
    if (type === "select_player") {
      if (selected !== null) onResolve({ value: String(selected) });
    } else if (type === "select_players") {
      if (multiSelected.length > 0) onResolve({ ids: multiSelected.map(String) });
    } else if (type === "select_card" || type === "select_option") {
      if (selected !== null) onResolve({ id: String(selected) });
    } else if (type === "rearrange") {
      const order = rearrangedOrder || options.map(o => o.id);
      onResolve({ order });
    }
    setSelected(null);
    setMultiSelected([]);
    setRearrangedOrder(null);
  };

  const moveItem = (arr, fromIdx, toIdx) => {
    const newArr = [...arr];
    const [item] = newArr.splice(fromIdx, 1);
    newArr.splice(toIdx, 0, item);
    return newArr;
  };

  const currentOrder = rearrangedOrder || options.map(o => o.id);

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent hideClose className="sm:max-w-md bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl border-2 border-white/40 rounded-2xl" data-testid="choice-modal">
        <DialogHeader>
          <DialogTitle className="text-[#1A237E] dark:text-[#E3F2FD] text-lg" style={{ fontFamily: 'Fredoka' }}>
            {card_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Acting player indicator */}
          <div className="flex items-center gap-2 text-sm text-[#1A237E]/70 dark:text-[#E3F2FD]/70">
            <User className="w-4 h-4" />
            <span className="font-semibold">{t("choiceModal.actingPlayer", { name: actingPlayer?.name })}</span>
          </div>

          <p className="text-sm text-[#1A237E]/80 dark:text-[#E3F2FD]/80">{prompt}</p>

          {/* ── Select Player ── */}
          {type === "select_player" && (
            <div className="grid grid-cols-2 gap-2">
              {options.map((pidx) => (
                <Button
                  key={pidx}
                  data-testid={`choose-player-${pidx}`}
                  variant={selected === pidx ? "default" : "outline"}
                  className={`rounded-xl h-12 ${selected === pidx ? 'bg-[#38B6FF] text-white' : 'border-[#1A237E]/20 dark:border-[#E3F2FD]/20 text-[#1A237E] dark:text-[#E3F2FD]'}`}
                  onClick={() => setSelected(pidx)}
                >
                  {players[pidx]?.name}
                </Button>
              ))}
            </div>
          )}

          {/* ── Select Multiple Players ── */}
          {type === "select_players" && (
            <div className="space-y-2">
              <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("choiceModal.selectPlayersRange", { min: data?.min || 1, max: data?.max || 3 })}</p>
              <div className="grid grid-cols-2 gap-2">
                {options.map((pidx) => {
                  const isSelected = multiSelected.includes(pidx);
                  return (
                    <Button
                      key={pidx}
                      data-testid={`choose-player-multi-${pidx}`}
                      variant={isSelected ? "default" : "outline"}
                      className={`rounded-xl h-12 ${isSelected ? 'bg-[#38B6FF] text-white' : 'border-[#1A237E]/20 dark:border-[#E3F2FD]/20 text-[#1A237E] dark:text-[#E3F2FD]'}`}
                      onClick={() => {
                        if (isSelected) {
                          setMultiSelected(multiSelected.filter(p => p !== pidx));
                        } else if (multiSelected.length < (data?.max || 3)) {
                          setMultiSelected([...multiSelected, pidx]);
                        }
                      }}
                    >
                      {isSelected && <Check className="w-4 h-4 mr-1" />}
                      {players[pidx]?.name}
                    </Button>
                  );
                })}
              </div>
            </div>
          )}

          {/* ── Select Card / Option ── */}
          {(type === "select_card" || type === "select_option") && (
            <div className="grid gap-2 max-h-60 overflow-y-auto">
              {options.map((opt) => (
                <Button
                  key={opt.id}
                  data-testid={`choose-option-${opt.id}`}
                  variant={selected === opt.id ? "default" : "outline"}
                  className={`rounded-xl h-10 justify-start text-left ${selected === opt.id ? 'bg-[#38B6FF] text-white' : 'border-[#1A237E]/20 dark:border-[#E3F2FD]/20 text-[#1A237E] dark:text-[#E3F2FD]'}`}
                  onClick={() => setSelected(opt.id)}
                >
                  {opt.label}
                </Button>
              ))}
            </div>
          )}

          {/* ── Rearrange ── */}
          {type === "rearrange" && (
            <div className="space-y-2">
              <p className="text-xs text-[#1A237E]/50 dark:text-[#E3F2FD]/50">{t("choiceModal.rearrangeHint")}</p>
              {currentOrder.map((id, idx) => {
                const opt = options.find(o => o.id === id);
                return (
                  <div key={id} className="flex items-center gap-2 bg-white/70 dark:bg-slate-900/70 rounded-xl p-2 border border-[#1A237E]/10 dark:border-[#E3F2FD]/10">
                    <span className="text-xs font-bold text-[#38B6FF] w-5">{idx + 1}</span>
                    <span className="text-sm text-[#1A237E] dark:text-[#E3F2FD] flex-1">{opt?.label}</span>
                    <Button
                      size="sm" variant="ghost" className="h-6 w-6 p-0"
                      disabled={idx === 0}
                      onClick={() => setRearrangedOrder(moveItem(currentOrder, idx, idx - 1))}
                    >
                      <ArrowUp className="w-3 h-3" />
                    </Button>
                    <Button
                      size="sm" variant="ghost" className="h-6 w-6 p-0"
                      disabled={idx === currentOrder.length - 1}
                      onClick={() => setRearrangedOrder(moveItem(currentOrder, idx, idx + 1))}
                    >
                      <ArrowDown className="w-3 h-3" />
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {/* Submit */}
          <Button
            data-testid="confirm-choice-btn"
            className="w-full rounded-full h-12 bg-[#38B6FF] hover:bg-[#2196F3] text-white font-bold text-base shadow-[0_4px_0_rgba(0,0,0,0.2)] hover:shadow-[0_2px_0_rgba(0,0,0,0.2)] hover:translate-y-[2px] transition-all active:scale-95"
            onClick={handleSubmit}
            disabled={loading || (
              type === "select_player" && selected === null ||
              type === "select_players" && multiSelected.length < (data?.min || 1) ||
              (type === "select_card" || type === "select_option") && selected === null
            )}
          >
            <Check className="w-5 h-5 mr-2" /> {t("choiceModal.button.confirm")}
          </Button>

          {/* Escape hatch — a mandatory choice modal otherwise blocks the
              header Surrender button, leaving no way to end the game early.
              Hidden in pass-and-play (no onSurrender passed). */}
          {onSurrender && (
            <button
              type="button"
              data-testid="choice-surrender-btn"
              onClick={onSurrender}
              disabled={loading}
              className="block w-full mt-1 text-xs text-[#FF5A5F]/80 hover:text-[#FF5A5F] underline-offset-2 hover:underline disabled:opacity-50"
            >
              {t("choiceModal.surrender")}
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
